
import {Module} from "../common/Module";
import {createPrivilegeIdentifier, isPrivileged, isPrivilegedByIdentifier} from "../../config/Privileges";
import { ProjectStageId, ProjectStageModule} from "./ProjectStage";
import {

    ProjectStagePhaseId,
    ProjectStagePhaseModule,
} from "./ProjectStagePhase";

import {Identifier} from "../common/Identifier";
import { AddressLocatable} from "../common/Address";
import {Money, Percent} from "../accounting/Finance";
import {Sensitive} from "../common/Sensitive";
import {Dateable, TimePeriodable} from "../common/TimePeriodable";
import {Nullable} from "../common/Nullable";
import {SoftDeletable} from "../common/SoftDeletable";
import {Describable, ShortDescribable} from "../common/Describable";
import {Budgetable, BudgetProp} from "../accounting/Budgetable";
import {Performanceable} from "../accounting/Performanceable";

import {ProjectEmployeeShort, ProjectStagePhaseTaskEmployee} from "./ProjectStagePhaseTaskEmployee";
import {AuthenticatedUser} from "../users/AuthenticatedUser";
import {ProjectCommissionState} from "./ProjectCommissionState";
import {ProjectCustomer} from "./ProjectCustomer";
import {ProjectUserPrivileges} from "./ProjectUserPrivileges";
import {ProjectStagePhaseTaskId, ProjectStagePhaseTaskModule} from "./ProjectStagePhaseTask";
import {ProjectResourceContainer} from "./ProjectResourceable";
import {BillSettingsProp} from "../accounting/BillSettings";
import {ProjectStatusProp} from "./ProjectStatusProp";

/**
 * Represents the name of the area where project-related data is managed.
 *
 * This value is used as a key or identifier to reference the section or grouping
 * of resources related to projects within the application. It serves as a
 * standardized constant for consistency across the codebase.
 *
 * Key considerations for developers:
 * - This value should remain consistent unless a larger refactor or renaming
 *   effort is undertaken.
 * - Modifying this value requires a thorough review to ensure all dependencies
 *   are updated appropriately.
 */
export const ProjectAreaName = "projects";

/**
 * Represents a module for managing privilege checks within the context of a project.
 * Serves as a core utility for verifying user permissions tied to specific project phases, stages, tasks, or the project itself.
 * Ensures that users have the appropriate access rights in various granular levels of a project's lifecycle.
 */
export interface IProjectModule extends Module {
    isPrivilegedPhase: (
        projectPrivileges: Nullable< ProjectUserPrivileges >,
        privilege: string,
        phaseIdPath: ProjectStagePhaseIdPath,
        authUser: Nullable< AuthenticatedUser >,
        privilegeIdentifier?: Nullable< string >
    ) => boolean
    isPrivilegedStage: (
        projectPrivileges: Nullable< ProjectUserPrivileges >,
        privilege: string,
        stageIdPath: ProjectStageIdPath,
        authUser: Nullable< AuthenticatedUser >,
        privilegeIdentifier?: Nullable< string >
    ) => boolean
    isPrivilegedProject: (
        projectPrivileges: Nullable< ProjectUserPrivileges >,
        privilege: string,
        projectId: Identifier< number >,
        authUser: Nullable< AuthenticatedUser >,
        privilegeIdentifier?: Nullable< string >
    ) => boolean
    isPrivilegedTask: (
        projectPrivileges: Nullable< ProjectUserPrivileges >,
        privilege: string,
        phaseIdPath: ProjectStagePhaseIdPath,
        authUser: Nullable< AuthenticatedUser >,
        privilegeIdentifier?: Nullable< string >
    ) => boolean
}

export const ProjectModule: IProjectModule = {
    identifier: "projects",
    /**
     * checks only NON Databased rights.
     * Most users won't have these rights for projects and the isPrivilegedFor should be used
     *
     * @param authUser
     * @param privilege
     */
    isPrivileged: (
            authUser,
            privilege,
            privilegeIdentifier?: string
    ) => {

        //console.log( "generalPrivileges" )

        if( typeof privilegeIdentifier === "undefined"  ) {
            return isPrivileged(authUser, ProjectAreaName, ProjectModule.identifier, privilege );
        } else {

            return isPrivilegedByIdentifier( authUser, privilegeIdentifier );
        }


    },

    isPrivilegedTask: (
        projectPrivileges: Nullable< ProjectUserPrivileges >,
        privilege: string,
        phaseIdPath: ProjectStagePhaseIdPath,
        authUser: Nullable< AuthenticatedUser >,
        privilegeIdentifier: Nullable< string > = null
    ) => {
        const privIdentifier = privilegeIdentifier ?? createPrivilegeIdentifier( ProjectAreaName, ProjectStagePhaseTaskModule.identifier, privilege );

        if( projectPrivileges === null ) return ProjectModule.isPrivileged( authUser, privilege, privIdentifier );

        if( projectPrivileges.privilegesByPhaseId.hasOwnProperty( phaseIdPath.phaseId.rawValue ) ) {
            const isPrivileged = projectPrivileges.privilegesByPhaseId[ phaseIdPath.phaseId.rawValue ][ privIdentifier ]?.isPermitted;

            if( isPrivileged ) {
                return isPrivileged;
            } else {
               // console.log("task", ProjectModule.isPrivilegedPhase( projectPrivileges, privilege, phaseIdPath, authUser, privIdentifier ))

                return ProjectModule.isPrivilegedPhase( projectPrivileges, privilege, phaseIdPath, authUser, privIdentifier );
            }
        } else {
            return ProjectModule.isPrivilegedPhase( projectPrivileges, privilege, phaseIdPath, authUser,  privIdentifier );
        }
    },

    /**
     * checks if the user is allowed to use/see a resource of a phase.
     *
     * Important:
     * when the given phaseId does not exist on ProjectPrivileges::privilegesByPhaseId,
     * the privilege will be checked on stage level next and lastly on project level.
     * This means that a privilege set at phase level will always be returned first
     *
     * order of privilege check for phaseResource is as follows
     * phase > stage > project
     *
     */
    isPrivilegedPhase: (
        projectPrivileges: Nullable< ProjectUserPrivileges >,
        privilege: string,
        phaseIdPath: ProjectStagePhaseIdPath,
        authUser: Nullable< AuthenticatedUser >,
        privilegeIdentifier: Nullable< string > = null
    ) => {
        const _privilegeIdentifier = privilegeIdentifier ?? createPrivilegeIdentifier( ProjectAreaName, ProjectStagePhaseModule.identifier, privilege );

        if( projectPrivileges === null ) return ProjectModule.isPrivileged( authUser, privilege, _privilegeIdentifier );

        if( projectPrivileges.privilegesByPhaseId.hasOwnProperty( phaseIdPath.phaseId.rawValue ) ) {

            const isPrivileged = projectPrivileges.privilegesByPhaseId[ phaseIdPath.phaseId.rawValue ][ _privilegeIdentifier ]?.isPermitted;

            if( isPrivileged ) {
                return isPrivileged;
            } else {
            //    console.log( "phase", ProjectModule.isPrivilegedStage(projectPrivileges, privilege, phaseIdPath, authUser, _privilegeIdentifier) )
                return ProjectModule.isPrivilegedStage(projectPrivileges, privilege, phaseIdPath, authUser, _privilegeIdentifier);
            }
        } else {
            return ProjectModule.isPrivilegedStage( projectPrivileges, privilege, phaseIdPath, authUser,  _privilegeIdentifier );
        }
    },
    /**
     * checks if the user is allowed to use/see a resource of a project
     *
     * @param projectPrivileges
     * @param privilege
     * @param projectId
     * @param privilegeIdentifier privilegeIdentifier supplied to check a specific identifier, prevents creation of an identifier during function runtime. null = identifier will be created as normal
     */
    isPrivilegedProject: (
        projectPrivileges,
        privilege,
        projectId,
        authUser,
        privilegeIdentifier: Nullable<string> = null,
    ) => {
        const _privilegeIdentifier = privilegeIdentifier ?? createPrivilegeIdentifier( ProjectAreaName, ProjectModule.identifier, privilege );
        const isPrivileged = projectPrivileges?.privileges[
            _privilegeIdentifier
            ]?.isPermitted;

        if( isPrivileged ) {
            return isPrivileged;
        } else {
          //  console.log("project", ProjectModule.isPrivileged( authUser, privilege, _privilegeIdentifier))
            return ProjectModule.isPrivileged( authUser, privilege, _privilegeIdentifier );
        }
    },
    /**
     * checks if the user is allowed to use/see a resource of a stage.
     *
     * Important:
     * when the given stageId does not exist on ProjectPrivileges::privilegesByStageId,
     * the privilege will be checked on project level next.
     * This means that a privilege set at stage level will always be returned first
     *
     * order of privilege check for stageResource is as follows
     * stage > project
     *
     * @param projectPrivileges
     * @param privilege
     * @param stageIdPath
     * @param privilegeIdentifier privilegeIdentifier supplied to check a specific identifier, prevents creation of an identifier during function runtime. null = identifier will be created as normal
     */
    isPrivilegedStage: (
        projectPrivileges,
        privilege,
        stageIdPath,
        authUser,
        privilegeIdentifier: Nullable<string> = null,
    ) => {
        if( projectPrivileges === null ) return false;

        const privilegeId = privilegeIdentifier ?? createPrivilegeIdentifier( ProjectAreaName, ProjectStageModule.identifier, privilege );

        if( projectPrivileges.privilegesByStageId.hasOwnProperty( stageIdPath.projectStageId.rawValue ) ) {
            const isPrivileged = projectPrivileges.privilegesByStageId[ stageIdPath.projectStageId.rawValue ][ privilegeId ]?.isPermitted;

            if( isPrivileged ) {
                return isPrivileged;
            } else {
                // console.log("stage",ProjectModule.isPrivilegedProject(projectPrivileges,privilege,stageIdPath.projectId,authUser,privilegeId) )
                return ProjectModule.isPrivilegedProject(
                    projectPrivileges,
                    privilege,
                    stageIdPath.projectId,
                    authUser,
                    privilegeId
                );
            }
        } else {
            return ProjectModule.isPrivilegedProject(
                projectPrivileges,
                privilege,
                stageIdPath.projectId,
                authUser,
                privilegeId
            );
        }
    }

};

/**
 * Represents the hierarchical identifiers to locate a specific phase within a project stage.
 *
 * This interface combines the unique identifiers for a project, the stage of the project,
 * and a specific phase within that stage. It ensures consistency and clarity when referencing
 * specific stages and phases of a project.
 *
 * By extending `ProjectId`, `ProjectStageId`, and `ProjectStagePhaseId`, this interface guarantees
 * that all required identifiers for hierarchical project navigation are included.
 * It is typically used for routing, data lookups, or API requests to precisely identify a
 * project stage phase within a larger context.
 *
 * @extends ProjectId Represents the unique identifier for the project.
 * @extends ProjectStageId Represents the unique identifier for the stage within the project.
 * @extends ProjectStagePhaseId Represents the unique identifier for the phase within the stage.
 */
export interface ProjectStagePhaseIdPath extends ProjectId, ProjectStageId, ProjectStagePhaseId {}

/**
 * Represents a mapping structure for the hierarchical phase identification path
 * within a project stage. This type ensures consistent tracking and reference
 * of phase identifiers as a structured property.
 *
 * This type is typically used in complex projects to maintain a clear and organized
 * representation of hierarchical phase relationships, supporting traceability
 * and clarity in workflows or project management tools.
 *
 * @property phaseIdPath - A structured property that contains the hierarchical path
 * of phase identifiers within a project stage. This allows for navigating and referencing
 * specific phases efficiently.
 */
export type ProjectStagePhaseIdPathProp = { phaseIdPath: ProjectStagePhaseIdPath }

/**
 * Represents a combined structure encapsulating the unique identifiers for a project and its associated stage.
 *
 * This interface is designed to ensure that any reference to a specific project stage includes both identifiers:
 * the project identifier and the stage identifier. It is typically used in APIs or internal data modeling
 * where both identifiers are required to uniquely locate or interact with a project stage*/
/**
 * Represents a combined structure encapsulating the unique identifiers for a project and its associated stage.
 *
 * This interface is designed to ensure that any reference to a specific project stage includes both identifiers:
 * the project identifier and the stage identifier. It is typically used in APIs or internal data modeling
 * where both identifiers are required to uniquely locate or interact with a project stage.
 *
 * @extends ProjectId Enforces the inclusion of a project-level unique identifier.
 * @extends ProjectStageId Enforces the inclusion of a stage-level unique identifier specific to the project.
 */
export interface ProjectStageIdPath extends ProjectId, ProjectStageId {}

/**
 * Represents the API route for selecting a project.
 *
 * This constant is used to construct API calls related to retrieving or operating on a selected project.
 * It ensures consistency across the application when interacting with the project's selection feature.
 *
 * @constant
 * @type {string}
 * @default "projects/select"
 */
export const PROJECT_SELECT_API_ROUTE = "projects/select";

/**
 * Represents an entity that can be associated with a specific project.
 *
 * This interface ensures that any implementing class or object defines a relationship to a project using a unique identifier.
 * It is intended to standardize the way project relations are modeled in the system and can be used across various
 * modules that need to interact with project-related data.
 *
 * @property projectsId - A unique numerical identifier for the associated project.
 */
export interface ProjectRelatable {
    projectsId: number
}

/**
 * Represents the detailed information about a specific project, including its core details and associated leads.
 *
 * This interface exists to facilitate structured representations of a project and its key stakeholders. It is used in scenarios where both project metadata and associated personnel information need to be processed or displayed together.
 */
export interface ProjectDetail {
    project: Project
    projectLeads: ProjectStagePhaseTaskEmployee[]

}

/**
 * Represents a concise view of a project within a project's ecosystem, typically used in summary lists or dashboards.
 *
 * This interface extends multiple base interfaces to consolidate relevant project-related information, such as
 * a short description, address location (if available), performance metrics, and time period boundaries.
 * It provides key metadata, including project leadership, budget conditions, and the current commission state.
 *
 * The structure is designed to support frontend and backend systems*/
/**
 * Represents a concise view of a project within a project's ecosystem, typically used in summary lists or dashboards.
 *
 * This interface extends multiple base interfaces to consolidate relevant project-related information, such as
 * a short description, address location (if available), performance metrics, and time period boundaries.
 * It provides key metadata, including project leadership, budget conditions, and the current commission state.
 *
 * The structure is designed to support frontend and backend systems that require a lightweight project representation
 * without unnecessary details.
 */
export interface ProjectListItem extends ProjectShort, ShortDescribable, Partial< AddressLocatable >, Performanceable, TimePeriodable<Nullable< Dateable > , Nullable< Dateable >>  {
    budgetCondition: Sensitive< Percent >
    legacyProjectNumber: Nullable<string>
    projectLeads: ProjectEmployeeShort[]
    currentCommissionState: ProjectCommissionState
}

/**
 * Represents a project with its associated metadata, customer, budget, and resource details.
 * This interface is designed to encapsulate all critical elements of a project for both business
 * management and technical implementations.
 *
 * Merges multiple behaviors from related interfaces to provide a unified structure for project data.
 * It supports operations like soft deletion, time period tracking, and resource allocation while fulfilling
 * financial and billing requirements.
 */
export interface Project extends Omit< ProjectListItem, 'budgetCondition' | 'currentCommissionState' >,
    Describable,
    SoftDeletable,
    BudgetProp,
    TimePeriodable< Nullable<Dateable>, Nullable<Dateable> >,
    ProjectResourceContainer,
    BillSettingsProp,
    ProjectStatusProp
{
    optionalBudget: Budgetable
    customer: ProjectCustomer
    resourceType: string
    additionalCostPrice?: Sensitive<Money>
    retailPriceWithAdditionalCosts?: Sensitive<Money>
    optionalRetailPriceWithAdditionalCosts?: Sensitive<Money>
    optionalAdditionalCosts?: Sensitive<Money>
}

/**
 * Constructs and returns a formatted project title for display purposes.
 *
 * This method combines the project's unique identifier and its description, making it easy to reference and identify the project at a glance.
 * It supports both detailed and minimal project objects.
 *
 * @param project - An object representing the project. It can either be a `Project` or a `ProjectShort`,
 * which must contain a `projectNumber` and a `shortDescription` property.
 *
 * @return A string formatted as "projectNumber - shortDescription", where `projectNumber` uniquely identifies the project
 * and `shortDescription` provides a brief summary of the project’s purpose.
 *
 * @throws {TypeError} If the `projectNumber` or `shortDescription` properties are missing or not strings,
 * this method will fail to process the input.
 */
export function projectTitle(project: Project | ProjectShort ) {
    return project.projectNumber + " - " + project.shortDescription;
}

/**
 * Represents a unique identifier for a Project.
 *
 * This interface is used to encapsulate the unique identity of a project
 * in a type-safe manner across the system, ensuring consistency and clarity
 * when referencing projects.
 *
 * The `projectId` is expected to be a numeric value wrapped in the `Identifier` type.
 * This approach allows for added flexibility, type-checking, and alignment with domain-driven design principles.
 */
export interface ProjectId {
    projectId: Identifier<number>
}

/**
 * Represents a concise view of a project with essential identifying details.
 *
 * This interface is designed to provide a minimal subset of project information
 * for scenarios where the full project details are not required, facilitating
 * lightweight data handling and improved performance in specific use cases.
 */
export interface ProjectShort extends ProjectId, ShortDescribable {
    projectNumber: string
}
